import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './pStyles/LandingPage.css';
import skillsbridgeLogo from '../img/skillsbridge.png';

// Interface for projects
interface Project {
  id: number;
  name: string;
  description: string;
  status: 'Active' | 'Completed' | 'Unassigned';
  created_at: string;
  participant_count?: number;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app';

const LandingPage: React.FC = () => {
  const [featuredProjects, setFeaturedProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  // Dynamic stats
  const [stats, setStats] = useState({
    volunteers: 0,
    projects: 0,
    hoursContributed: 0
  });

  const testimonials = [
    {
      quote: "The health outreach program has been incredibly rewarding. Seeing the direct impact we're making is what keeps me motivated.",
      author: "Sarah M, Health Volunteer"
    },
    {
      quote: "Working with local schools has opened my eyes to the challenges our children face. Every volunteer hour counts toward a brighter future.",
      author: "Michael K, Education Coordinator"
    },
    {
      quote: "SkillsBridge connected me with like-minded individuals who share my passion for environmental conservation. Together, we're making real change.",
      author: "Alex R, Environmental Advocate"
    }
  ];

  // Animation on scroll effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const heroSection = document.querySelector('.hero-section');
    if (heroSection) {
      observer.observe(heroSection);
    }

    return () => observer.disconnect();
  }, []);

  // Testimonial rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  // Animate stats
  useEffect(() => {
    if (featuredProjects.length > 0) {
      const totalVolunteers = featuredProjects.reduce((sum, project) => sum + (project.participant_count || 0), 0);
      
      // Animate numbers
      const animateValue = (start: number, end: number, setValue: (value: number) => void, duration: number = 2000) => {
        const startTime = Date.now();
        const animate = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const current = Math.floor(start + (end - start) * progress);
          setValue(current);
          
          if (progress < 1) {
            requestAnimationFrame(animate);
          }
        };
        animate();
      };

      setTimeout(() => {
        animateValue(0, totalVolunteers + 847, (val) => setStats(prev => ({ ...prev, volunteers: val })), 1500);
        animateValue(0, featuredProjects.length + 12, (val) => setStats(prev => ({ ...prev, projects: val })), 1800);
        animateValue(0, 15420, (val) => setStats(prev => ({ ...prev, hoursContributed: val })), 2200);
      }, 500);
    }
  }, [featuredProjects]);

  // Fetch featured projects
  useEffect(() => {
    const fetchFeaturedProjects = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await axios.get<Project[]>(`${API_BASE_URL}/api/projects`, {
          headers: {
            'user-type': 'public'
          }
        });
        const activeProjects = response.data.filter(project => 
          project.status === 'Active'
        ).slice(0, 3);

        const projectsWithCounts = await Promise.all(
          activeProjects.map(async (project) => {
            try {
              const countResponse = await axios.get(`${API_BASE_URL}/api/projects/${project.id}/participants/count`);
              return {
                ...project,
                participant_count: (countResponse.data as { count: number }).count || 0
              };
            } catch (error) {
              console.error(`Error fetching participant count for project ${project.id}:`, error);
              return { ...project, participant_count: 0 };
            }
          })
        );

        setFeaturedProjects(projectsWithCounts);
      } catch (err) {
        console.error('Error fetching featured projects:', err);
        setError('Could not load featured projects at this time.');
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedProjects();
  }, []);

  return (
    <div className="landing-page">
      <main className="landing-main">
        {/* --- HERO SECTION --- */}
        <section className={`hero-section ${isVisible ? 'animate-in' : ''}`}>
          <div className="hero-background-pattern"></div>
          <div className="landing-container">
            <div className="hero-content">
              <div className="hero-text-content">
                <h1 className="hero-title">
                  Connecting Skills, <span className="highlight-text">Creating Futures.</span>
                </h1>
                <p className="hero-subtitle">
                  SkillsBridge connects passionate volunteers with projects that build stronger communities. Find your purpose here.
                </p>
                <div className="hero-cta">
                  <Link to="/application" className="cta-button primary">
                    Start Your Journey
                    <span className="button-arrow">→</span>
                  </Link>
                  <Link to="/projects" className="cta-button secondary">
                    Explore Projects
                  </Link>
                </div>
              </div>
              <div className="hero-visual">
                <div className="hero-image-container">
                  <img 
                    src={skillsbridgeLogo}
                    alt="SkillsBridge - Connecting volunteers with community projects"
                    className="hero-image"
                    onError={(e) => {
                      console.error('Image failed to load');
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  {/* WhatsApp Community CTA */}
                  <a
                    href="https://chat.whatsapp.com/FRHsVwvhZJKDVQi6hYy89r?mode=wwt"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whatsapp-cta"
                    aria-label="Join our WhatsApp community"
                    title="Join our WhatsApp community"
                  >
                    {/* WhatsApp SVG icon */}
                    <svg viewBox="0 0 32 32" className="whatsapp-icon" aria-hidden="true">
                      <path fill="#25D366" d="M19.11 17.27c-.29-.14-1.66-.82-1.92-.91-.26-.1-.45-.14-.64.14-.19.29-.73.9-.9 1.08-.17.19-.33.21-.62.07-.29-.14-1.23-.45-2.35-1.45-.87-.77-1.45-1.72-1.62-2-.17-.29-.02-.45.12-.59.12-.12.29-.33.43-.5.14-.17.19-.29.29-.48.1-.19.05-.36-.02-.5-.07-.14-.64-1.54-.88-2.11-.23-.55-.47-.48-.64-.48-.17 0-.36-.02-.55-.02-.19 0-.5.07-.76.36-.26.29-1 1-.1 2.45.9 1.45 2.09 2.61 2.4 2.9.31.29 4.12 3.14 6.86 4.08.96.33 1.71.52 2.29.67.96.23 1.84.2 2.53.12.77-.09 2.36-1 2.69-1.97.33-.98.33-1.82.24-2-.09-.17-.26-.26-.55-.4-.29-.14-1.66-.82-1.92-.91z"/>
                      <path fill="#25D366" d="M16 3C9.38 3 4 8.38 4 15c0 2.65.88 5.1 2.36 7.07L5 29l7.12-1.31C13.99 27.9 14.98 28 16 28c6.62 0 12-5.38 12-12S22.62 3 16 3zm0 22.5c-.98 0-1.93-.16-2.82-.46l-.2-.07-4.2.77.78-4.09-.1-.21C8.5 19.53 8 17.82 8 16 8 10.48 12.48 6 18 6s10 4.48 10 10-4.48 9.5-10 9.5z"/>
                    </svg>
                    <div className="whatsapp-text">
                      <strong>Join our WhatsApp community</strong>
                      <span>Tap to connect with volunteers</span>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          {/* Dynamic Stats Bar */}
          <div className="stats-bar">
            <div className="landing-container">
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-emoji">👥</span>
                  <div className="stat-content">
                    <div className="stat-number">{stats.volunteers.toLocaleString()}+</div>
                    <div className="stat-label">Active Volunteers</div>
                  </div>
                </div>
                <div className="stat-item">
                  <span className="stat-emoji">📋</span>
                  <div className="stat-content">
                    <div className="stat-number">{stats.projects}+</div>
                    <div className="stat-label">Active Projects</div>
                  </div>
                </div>
                <div className="stat-item">
                  <span className="stat-emoji">⏱️</span>
                  <div className="stat-content">
                    <div className="stat-number">{stats.hoursContributed.toLocaleString()}+</div>
                    <div className="stat-label">Hours Contributed</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* --- "GET STARTED" SECTION --- */}
        <section className="page-section">
          <div className="landing-container">
            <h2 className="section-title">Get Started in Minutes</h2>
            <div className="steps-container">
              <div className="step-item">
                <div className="step-circle">
                  <span>1</span>
                </div>
                <h3 className="step-title">Click Sign Up</h3>
                <p className="step-description">Create your account and join our community.</p>
              </div>
              <div className="step-item">
                <div className="step-circle">
                  <span>2</span>
                </div>
                <h3 className="step-title">Add Your Details</h3>
                <p className="step-description">Tell us about your interests and skills.</p>
              </div>
              <div className="step-item">
                <div className="step-circle">
                  <span>3</span>
                </div>
                <h3 className="step-title">Finalize Your Registration</h3>
                <p className="step-description">We'll verify your information and complete your registration.</p>
              </div>
              <div className="step-item">
                <div className="step-circle">
                  <span>4</span>
                </div>
                <h3 className="step-title">Make a Difference</h3>
                <p className="step-description">Start volunteering and creating impact.</p>
              </div>
            </div>
          </div>
        </section>

        {/* --- "CURRENT OPPORTUNITIES" SECTION --- */}
        <section className="page-section alt-background">
          <div className="landing-container">
            <h2 className="section-title">Current Opportunities</h2>
            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <div className="loading-message">Loading featured projects...</div>
              </div>
            ) : error ? (
              <div className="error-message">{error}</div>
            ) : featuredProjects.length === 0 ? (
              <div className="no-projects-message">
                <p>No active projects available at the moment.</p>
                <Link to="/projects" className="view-all-link">Check back soon for new opportunities</Link>
              </div>
            ) : (
              <div className="featured-projects">
                {featuredProjects.map((project, index) => (
                  <div key={project.id} className={`featured-project-card fade-in-up delay-${index}`}>
                    <div className="project-header">
                      <h3 className="project-title">{project.name}</h3>
                      <div className="volunteer-count-container">
                        <div className="pulse-dot"></div>
                        <span className="volunteer-count">{project.participant_count || 0} volunteers</span>
                      </div>
                    </div>
                    <p className="project-description">{project.description}</p>
                    <div className="project-footer">
                      <span className="status-badge">
                        <span className="status-dot"></span>
                        Active
                      </span>
                      <Link to="/projects" className="learn-more-btn">
                        Learn More
                        <span className="btn-arrow">→</span>
                      </Link>
                    </div>
                  </div>
                ))}
                <div className="view-all-container">
                  <Link to="/projects" className="view-all-projects-btn">
                    View All Projects
                  </Link>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* --- "COMMUNITY VOICES" SECTION --- */}
        <section className="page-section">
          <div className="landing-container">
            <h2 className="section-title">Community Voices</h2>
            <div className="dynamic-testimonial">
              <div className="testimonial-container">
                <div className="testimonial-content">
                  <p className="testimonial-quote">"{testimonials[currentTestimonial].quote}"</p>
                  <footer className="testimonial-author">{testimonials[currentTestimonial].author}</footer>
                </div>
              </div>
              <div className="testimonial-dots">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    className={`dot ${index === currentTestimonial ? 'active' : ''}`}
                    onClick={() => setCurrentTestimonial(index)}
                    aria-label={`View testimonial ${index + 1}`}
                  />
                ))}
              </div>
            </div>
            
            {/* Static testimonials below */}
            <div className="testimonial-cards">
              <div className="testimonial-card">
                <div className="testimonial-avatar">
                  <div className="avatar-icon">SM</div>
                </div>
                <p className="testimonial-quote">"The health outreach program has been incredibly rewarding. Seeing the direct impact we're making is what keeps me motivated."</p>
                <footer className="testimonial-author">Sarah M, Health Volunteer</footer>
              </div>
              <div className="testimonial-card">
                <div className="testimonial-avatar">
                  <div className="avatar-icon">MK</div>
                </div>
                <p className="testimonial-quote">"Working with local schools has opened my eyes to the challenges our children face. Every volunteer hour counts toward a brighter future."</p>
                <footer className="testimonial-author">Michael K, Education Coordinator</footer>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* --- FOOTER --- */}
      <footer className="landing-footer">
        <div className="landing-container">
          <div className="footer-content">
            <div className="footer-logo">
              <span className="logo-text">SkillsBridge</span>
            </div>
            <p>&copy; 2025 SkillsBridge. Making a difference together.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;